import { Link } from "react-router-dom";
import { ArrowRight } from "lucide-react";

import { BODY, C, DISPLAY, cardBodyStyle, cardLight, cardTitleStyle } from "../../pages/Landing";
import { FEATURES, TIER } from "./features";

const TIER_STYLE = {
  free: { bg: "rgba(46,125,82,0.12)", fg: "#2E7D52" },
  plus: { bg: "#E6F0FA", fg: "#1D4E89" },
  board: { bg: "#1f3b2d", fg: "#fff" },
  league: { bg: "#FAEEDA", fg: "#854F0B" },
};

export function TierBadge({ tier }) {
  const s = TIER_STYLE[tier];
  return (
    <span data-testid={`tier-badge-${tier}`} style={{
      fontFamily: BODY, fontWeight: 700, fontSize: 10.5, letterSpacing: "0.12em", textTransform: "uppercase",
      background: s.bg, color: s.fg, padding: "4px 9px", borderRadius: 999, whiteSpace: "nowrap",
    }}>{TIER[tier]}</span>
  );
}

export function PhoneShot({ shot, testId, tilt = 0 }) {
  return (
    <figure data-testid={testId} className="mk-phone" style={{ margin: 0, transform: `rotate(${tilt}deg)` }}>
      <img src={shot.src} alt={shot.alt} width={390} height={844} loading="lazy" decoding="async"
           style={{ display: "block", width: "100%", height: "auto", borderRadius: 26 }} />
    </figure>
  );
}

export function ShotSteps({ steps, idPrefix }) {
  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 72, marginTop: 56 }}>
      {steps.map((s, i) => (
        <div key={s.title} className={`mk-step ${i % 2 ? "mk-step--flip" : ""}`} data-testid={`${idPrefix}-step-${i + 1}`}>
          <div className="mk-step-text">
            <div style={{ fontFamily: DISPLAY, fontSize: 56, color: C.greenMid, lineHeight: 0.9 }}>{String(i + 1).padStart(2, "0")}</div>
            <div style={{ display: "flex", gap: 10, alignItems: "center", flexWrap: "wrap", marginTop: 10 }}>
              <h3 style={{ ...cardTitleStyle, fontSize: 30, margin: 0 }}>{s.title}</h3>
              {s.tier && <TierBadge tier={s.tier} />}
            </div>
            <p style={{ ...cardBodyStyle, fontSize: 16, maxWidth: 460, marginTop: 12 }}>{s.body}</p>
          </div>
          <div className="mk-step-shot"><PhoneShot shot={s.shot} testId={`${idPrefix}-shot-${i + 1}`} tilt={i % 2 ? 1.5 : -1.5} /></div>
        </div>
      ))}
      <MarketingStyles />
    </div>
  );
}

export function FeatureGrid({ keys, idPrefix }) {
  return (
    <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(min(100%, 260px), 1fr))", gap: 14, marginTop: 44 }}>
      {keys.map((k) => {
        const f = FEATURES[k];
        return (
          <div key={k} className="chalk-marketing-card" style={cardLight} data-testid={`${idPrefix}-feature-${k}`}>
            <TierBadge tier={f.tier} />
            <div style={{ ...cardTitleStyle, marginTop: 14 }}>{f.name}</div>
            <p style={cardBodyStyle}>{f.body}</p>
          </div>
        );
      })}
    </div>
  );
}

export function AudienceCards() {
  const items = [
    ["/coaches", "Coaches", "Schedule, Erase and Redraw, Chalk Talk and Draw It Up."],
    ["/parents", "Parents", "Your kids' plans, one-tap RSVPs and alerts that actually reach you."],
    ["/team-staff", "Team staff", "Team Moms, Dads and coordinators with the access the coach picks."],
    ["/leagues", "Leagues", "League-wide broadcasts and an overview of every team."],
  ];
  return (
    <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(min(100%, 230px), 1fr))", gap: 14, marginTop: 44 }}>
      {items.map(([to, name, body]) => (
        <Link key={to} to={to} className="chalk-marketing-card" data-testid={`audience-card-${to.slice(1)}`}
              style={{ ...cardLight, textDecoration: "none", color: C.textDark, display: "block" }}>
          <div style={{ ...cardTitleStyle, fontSize: 26, marginTop: 0 }}>For {name.toLowerCase()}</div>
          <p style={cardBodyStyle}>{body}</p>
          <span style={{ fontFamily: BODY, fontWeight: 700, fontSize: 12, letterSpacing: "0.12em", textTransform: "uppercase", color: C.greenMid, display: "inline-flex", gap: 6, alignItems: "center", marginTop: 8 }}>
            See how <ArrowRight size={13} aria-hidden="true" />
          </span>
        </Link>
      ))}
    </div>
  );
}

function MarketingStyles() {
  return (
    <style>{`
      .mk-phone { max-width: 280px; margin: 0 auto; padding: 8px; background: #111; border-radius: 34px;
        box-shadow: 0 30px 60px rgba(26,58,42,0.28); transition: transform 300ms ease; }
      .mk-phone:hover { transform: rotate(0deg) translateY(-4px) !important; }
      .mk-step { display: flex; flex-direction: column; gap: 32px; }
      @media (min-width: 860px) {
        .mk-step { display: grid; grid-template-columns: 1.1fr 0.9fr; gap: 64px; align-items: center; }
        .mk-step--flip .mk-step-text { order: 2; }
        .mk-step--flip .mk-step-shot { order: 1; }
      }
    `}</style>
  );
}
